import { useQuery } from "@tanstack/react-query"

/**
 * Leistungsspektrum-Ampel in der Metaspalte der Mail (#82).
 *
 * Robin (Sanitec, 08.09.2026): „Feld ‚Leistungsspektrum‘ am Rand wäre
 * hilfreich, um auf einen Blick sehen zu können, liegt die Anfrage im
 * Leistungsspektrum oder nicht. Grüner Haken, wenn die letzten Male immer ein
 * Auftrag entstand; gelb, wenn die KI es nicht einschätzen kann; rot, wenn
 * diese Art Anfrage die letzten Male immer abgelehnt wurde.“
 *
 * Die Regel steht im Backend (services/leistungsspektrum.py); hier wird nur
 * gezeigt, was sie sagt — und wo die Liste dazu gepflegt wird.
 */
type Ampel = {
  farbe: "gruen" | "gelb" | "rot" | "weiterleiten"
  grund: string
  anfrageart: string
  quelle: "historie" | "liste" | "keine" | "weiterleitung"
  liste_gepflegt: boolean
}

const FARBE: Record<Ampel["farbe"], { text: string; klasse: string; symbol: string }> = {
  gruen: { text: "Im Leistungsspektrum", klasse: "ev2-ampel-gruen", symbol: "✓" },
  gelb: { text: "Nicht einschätzbar", klasse: "ev2-ampel-gelb", symbol: "?" },
  rot: { text: "Nicht im Leistungsspektrum", klasse: "ev2-ampel-rot", symbol: "✕" },
  // #139: passt auf eine Weiterleitungsregel (z. B. Lohnabrechnung → Steuerbüro)
  weiterleiten: { text: "Weiterleiten", klasse: "ev2-ampel-gruen", symbol: "↪" },
}

export function LeistungsspektrumBlock({ emailId, tenantSlug }: { emailId: number; tenantSlug?: string | null }) {
  const q = useQuery<Ampel>({
    queryKey: ["leistungsspektrum", emailId],
    queryFn: async () => {
      const r = await fetch(`/api/v1/emails/${emailId}/leistungsspektrum`, { credentials: "include" })
      if (!r.ok) throw new Error(`HTTP ${r.status}`)
      return r.json()
    },
    enabled: !!emailId,
    staleTime: 1000 * 60 * 5,
  })
  const pflegeHref = `/context${tenantSlug ? `?tenant=${encodeURIComponent(tenantSlug)}` : ""}`

  // Paul 14.10.2026: „Leistungsspektrum – wird das überhaupt gebraucht?" Nur,
  // wenn es etwas sagt: „nicht im Spektrum" oder „weiterleiten". Grün und
  // „nicht einschätzbar" waren an fast jeder Mail Ballast. Fehler bleiben
  // sichtbar — eine Messlücke soll nicht wie „alles gut" aussehen.
  if (q.data && q.data.farbe !== "rot" && q.data.farbe !== "weiterleiten") return null
  if (q.isLoading) return null

  return (
    <div className="ev2-meta-block" data-testid="leistungsspektrum">
      <div className="ev2-meta-label">Leistungsspektrum</div>
      {q.isLoading && (
        <div style={{ fontSize: 12.5, color: "var(--text-muted)" }}>wird geprüft …</div>
      )}
      {q.isError && (
        // Eine Messlücke darf nicht wie ein Ergebnis aussehen — also keine
        // gelbe Ampel bei Fehler, sondern die Meldung.
        <div style={{ fontSize: 12.5, color: "var(--text-muted)" }}>Konnte nicht geprüft werden.</div>
      )}
      {q.data && (
        <>
          <span className={`ev2-ampel ${FARBE[q.data.farbe]?.klasse || "ev2-ampel-gelb"}`}
                title={q.data.quelle === "historie" ? "Aus der Historie des Betriebs" : q.data.quelle === "liste" ? "Aus der gepflegten Liste" : q.data.quelle === "weiterleitung" ? "Aus den Weiterleitungen im Kontext-Center" : "Weder Historie noch Liste"}>
            <span aria-hidden="true">{FARBE[q.data.farbe]?.symbol}</span>
            {FARBE[q.data.farbe]?.text}
            {q.data.anfrageart ? ` · ${q.data.anfrageart}` : ""}
          </span>
          <div style={{ fontSize: 12.5, color: "var(--text-secondary)", lineHeight: 1.6, marginTop: 8 }}>
            {q.data.grund}
          </div>
          <div style={{ fontSize: 12, marginTop: 6, display: "flex", gap: 10, flexWrap: "wrap" }}>
            <a href={pflegeHref} style={{ color: "var(--accent-color)" }}>
              {q.data.liste_gepflegt ? "Liste im Kontext-Center" : "Liste im Kontext-Center anlegen"}
            </a>
            {/* Vorgaben je Betrieb (#83): dort steht auch, wie die KI bei
                diesem Betrieb vorgehen soll — Kontext-Center → Aktive Regeln. */}
            <a href={pflegeHref} style={{ color: "var(--text-muted)" }} title="Kontext-Center → Aktive Regeln">
              Vorgaben für die KI
            </a>
          </div>
        </>
      )}
    </div>
  )
}
